package org.chiangmeng.qfast.database

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Long
import kotlin.String

public class QueueHistoryQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> selectHistoryByShopId(shop_id: String, mapper: (
    id: Long,
    shop_id: String,
    ticket_number: Long,
    served_at: Long,
  ) -> T): Query<T> = SelectHistoryByShopIdQuery(shop_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!,
      cursor.getLong(3)!!
    )
  }

  public fun selectHistoryByShopId(shop_id: String): Query<QueueHistoryEntity> =
      selectHistoryByShopId(shop_id) { id, shop_id_, ticket_number, served_at ->
    QueueHistoryEntity(
      id,
      shop_id_,
      ticket_number,
      served_at
    )
  }

  public fun <T : Any> selectActiveQueuesByShopId(shop_id: String, mapper: (
    shop_id: String,
    ticket_number: Long,
    category: String,
    status: String,
    time: String,
    call_time: String?,
    pre_order: String?,
  ) -> T): Query<T> = SelectActiveQueuesByShopIdQuery(shop_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getLong(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6)
    )
  }

  public fun selectActiveQueuesByShopId(shop_id: String): Query<LocalActiveQueueEntity> =
      selectActiveQueuesByShopId(shop_id) { shop_id_, ticket_number, category, status, time,
      call_time, pre_order ->
    LocalActiveQueueEntity(
      shop_id_,
      ticket_number,
      category,
      status,
      time,
      call_time,
      pre_order
    )
  }

  public fun <T : Any> selectShopState(shop_id: String, mapper: (
    shop_id: String,
    last_ticket: Long,
    current_servings_json: String,
  ) -> T): Query<T> = SelectShopStateQuery(shop_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getLong(1)!!,
      cursor.getString(2)!!
    )
  }

  public fun selectShopState(shop_id: String): Query<LocalShopStateEntity> =
      selectShopState(shop_id) { shop_id_, last_ticket, current_servings_json ->
    LocalShopStateEntity(
      shop_id_,
      last_ticket,
      current_servings_json
    )
  }

  public fun insertHistory(
    shop_id: String,
    ticket_number: Long,
    served_at: Long,
  ) {
    driver.execute(-1_869_285_591,
        """INSERT INTO QueueHistoryEntity (shop_id, ticket_number, served_at) VALUES (?, ?, ?)""",
        3) {
          bindString(0, shop_id)
          bindLong(1, ticket_number)
          bindLong(2, served_at)
        }
    notifyQueries(-1_869_285_591) { emit ->
      emit("QueueHistoryEntity")
    }
  }

  public fun insertActiveQueue(
    shop_id: String,
    ticket_number: Long,
    category: String,
    status: String,
    time: String,
    call_time: String?,
    pre_order: String?,
  ) {
    driver.execute(1_832_967_616, """
        |INSERT OR REPLACE INTO LocalActiveQueueEntity (shop_id, ticket_number, category, status, time, call_time, pre_order)
        |VALUES (?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 7) {
          bindString(0, shop_id)
          bindLong(1, ticket_number)
          bindString(2, category)
          bindString(3, status)
          bindString(4, time)
          bindString(5, call_time)
          bindString(6, pre_order)
        }
    notifyQueries(1_832_967_616) { emit ->
      emit("LocalActiveQueueEntity")
    }
  }

  public fun updateActiveQueueServing(
    call_time: String?,
    shop_id: String,
    ticket_number: Long,
  ) {
    driver.execute(1_667_144_764,
        """UPDATE LocalActiveQueueEntity SET status = 'serving', call_time = ? WHERE shop_id = ? AND ticket_number = ?""",
        3) {
          bindString(0, call_time)
          bindString(1, shop_id)
          bindLong(2, ticket_number)
        }
    notifyQueries(1_667_144_764) { emit ->
      emit("LocalActiveQueueEntity")
    }
  }

  public fun updateActiveQueueStatus(
    status: String,
    shop_id: String,
    ticket_number: Long,
  ) {
    driver.execute(-1_041_254_974,
        """UPDATE LocalActiveQueueEntity SET status = ? WHERE shop_id = ? AND ticket_number = ?""",
        3) {
          bindString(0, status)
          bindString(1, shop_id)
          bindLong(2, ticket_number)
        }
    notifyQueries(-1_041_254_974) { emit ->
      emit("LocalActiveQueueEntity")
    }
  }

  public fun updateActiveQueueCallTime(
    call_time: String?,
    shop_id: String,
    ticket_number: Long,
  ) {
    driver.execute(-1_409_969_125,
        """UPDATE LocalActiveQueueEntity SET call_time = ? WHERE shop_id = ? AND ticket_number = ?""",
        3) {
          bindString(0, call_time)
          bindString(1, shop_id)
          bindLong(2, ticket_number)
        }
    notifyQueries(-1_409_969_125) { emit ->
      emit("LocalActiveQueueEntity")
    }
  }

  public fun deleteActiveQueue(shop_id: String, ticket_number: Long) {
    driver.execute(-529_780_850,
        """DELETE FROM LocalActiveQueueEntity WHERE shop_id = ? AND ticket_number = ?""", 2) {
          bindString(0, shop_id)
          bindLong(1, ticket_number)
        }
    notifyQueries(-529_780_850) { emit ->
      emit("LocalActiveQueueEntity")
    }
  }

  public fun deleteActiveQueuesByShopId(shop_id: String) {
    driver.execute(-859_649_107, """DELETE FROM LocalActiveQueueEntity WHERE shop_id = ?""", 1) {
          bindString(0, shop_id)
        }
    notifyQueries(-859_649_107) { emit ->
      emit("LocalActiveQueueEntity")
    }
  }

  public fun deletePreviousServing(
    shop_id: String,
    category: String,
    ticket_number: Long,
  ) {
    driver.execute(-2_058_413_704,
        """DELETE FROM LocalActiveQueueEntity WHERE shop_id = ? AND category = ? AND status = 'serving' AND ticket_number != ?""",
        3) {
          bindString(0, shop_id)
          bindString(1, category)
          bindLong(2, ticket_number)
        }
    notifyQueries(-2_058_413_704) { emit ->
      emit("LocalActiveQueueEntity")
    }
  }

  public fun insertOrReplaceShopState(
    shop_id: String,
    last_ticket: Long,
    current_servings_json: String,
  ) {
    driver.execute(204_387_637, """
        |INSERT OR REPLACE INTO LocalShopStateEntity (shop_id, last_ticket, current_servings_json)
        |VALUES (?, ?, ?)
        """.trimMargin(), 3) {
          bindString(0, shop_id)
          bindLong(1, last_ticket)
          bindString(2, current_servings_json)
        }
    notifyQueries(204_387_637) { emit ->
      emit("LocalShopStateEntity")
    }
  }

  public fun updateLastTicket(last_ticket: Long, shop_id: String) {
    driver.execute(-835_867_491,
        """UPDATE LocalShopStateEntity SET last_ticket = ? WHERE shop_id = ?""", 2) {
          bindLong(0, last_ticket)
          bindString(1, shop_id)
        }
    notifyQueries(-835_867_491) { emit ->
      emit("LocalShopStateEntity")
    }
  }

  public fun updateCurrentServingsJson(current_servings_json: String, shop_id: String) {
    driver.execute(76_021_389,
        """UPDATE LocalShopStateEntity SET current_servings_json = ? WHERE shop_id = ?""", 2) {
          bindString(0, current_servings_json)
          bindString(1, shop_id)
        }
    notifyQueries(76_021_389) { emit ->
      emit("LocalShopStateEntity")
    }
  }

  public fun resetShopState(shop_id: String) {
    driver.execute(244_895_966,
        """UPDATE LocalShopStateEntity SET last_ticket = 0, current_servings_json = '{}' WHERE shop_id = ?""",
        1) {
          bindString(0, shop_id)
        }
    notifyQueries(244_895_966) { emit ->
      emit("LocalShopStateEntity")
    }
  }

  private inner class SelectHistoryByShopIdQuery<out T : Any>(
    public val shop_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("QueueHistoryEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("QueueHistoryEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-557_868_146,
        """SELECT QueueHistoryEntity.id, QueueHistoryEntity.shop_id, QueueHistoryEntity.ticket_number, QueueHistoryEntity.served_at FROM QueueHistoryEntity WHERE shop_id = ?""",
        mapper, 1) {
      bindString(0, shop_id)
    }

    override fun toString(): String = "QueueHistory.sq:selectHistoryByShopId"
  }

  private inner class SelectActiveQueuesByShopIdQuery<out T : Any>(
    public val shop_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("LocalActiveQueueEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("LocalActiveQueueEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_231_837_058,
        """SELECT LocalActiveQueueEntity.shop_id, LocalActiveQueueEntity.ticket_number, LocalActiveQueueEntity.category, LocalActiveQueueEntity.status, LocalActiveQueueEntity.time, LocalActiveQueueEntity.call_time, LocalActiveQueueEntity.pre_order FROM LocalActiveQueueEntity WHERE shop_id = ? ORDER BY ticket_number ASC""",
        mapper, 1) {
      bindString(0, shop_id)
    }

    override fun toString(): String = "QueueHistory.sq:selectActiveQueuesByShopId"
  }

  private inner class SelectShopStateQuery<out T : Any>(
    public val shop_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("LocalShopStateEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("LocalShopStateEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(756_518_317,
        """SELECT LocalShopStateEntity.shop_id, LocalShopStateEntity.last_ticket, LocalShopStateEntity.current_servings_json FROM LocalShopStateEntity WHERE shop_id = ?""",
        mapper, 1) {
      bindString(0, shop_id)
    }

    override fun toString(): String = "QueueHistory.sq:selectShopState"
  }
}
